import React, { forwardRef, useId, useState } from "react";

import { cn } from "src/shadcn/lib/utils";
import {
  Field,
  FieldLabel,
  FieldContent,
  FieldDescription,
  FieldError,
} from "src/primitives/Field";
import { Textarea as PrimitiveTextarea } from "src/primitives/Textarea";

import { SIZE_ROWS, SIZE_MIN_HEIGHT } from "./constants";
import { getTrimmedValue } from "./utils";

import type { TextareaSize } from "./constants";

type TextareaResize = "vertical" | "none";

type PrimitiveTextareaProps = React.ComponentProps<typeof PrimitiveTextarea>;

export interface TextareaProps extends Omit<
  PrimitiveTextareaProps,
  "size" | "children" | "prefix" | "value"
> {
  /** Controlled value. */
  value?: string | number;
  /** Size of the textarea. Controls default rows: small=1, medium=3, large=4. */
  size?: TextareaSize;
  /** Resize behavior. */
  resize?: TextareaResize;
  /** Label displayed above the textarea. */
  label?: string;
  /** Error message displayed below the textarea. */
  error?: string;
  /** Help text displayed below the textarea. Accepts string or ReactNode. */
  helpText?: React.ReactNode;
  /** Maximum character limit. Counter visible at 85% capacity. */
  maxLength?: number;
  /** Allow typing past maxLength, show error styling on count. */
  unlimitedChars?: boolean;
  /** Prevent trimming whitespace on blur. */
  disableTrimOnBlur?: boolean;
  /** Content to display before the textarea. */
  prefix?: React.ReactNode;
  /** Content to display after the textarea. */
  suffix?: React.ReactNode;
  /** Render the textarea without borders. */
  nakedTextarea?: boolean;
  /** Props forwarded to the Label element. */
  labelProps?: React.ComponentProps<typeof FieldLabel>;
  /** Additional class name for the outermost wrapper. */
  className?: string;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      size = "medium",
      resize = "vertical",
      label = "",
      error = "",
      helpText = "",
      disabled = false,
      required = false,
      maxLength,
      unlimitedChars = false,
      disableTrimOnBlur = false,
      prefix,
      suffix,
      nakedTextarea = false,
      labelProps,
      className = "",
      rows: rowsProp,
      onBlur,
      onChange,
      value: controlledValue,
      ...otherProps
    },
    ref
  ) => {
    const generatedId = useId();
    const id = otherProps.id ?? generatedId;
    const errorId = `error_${id}`;
    const helpTextId = `helpText_${id}`;

    // Controlled / uncontrolled value tracking
    const [valueInternal, setValueInternal] = useState(controlledValue ?? "");
    const isControlled = controlledValue !== undefined;

    const value = isControlled ? controlledValue : valueInternal;
    const valueLength = value?.toString().length || 0;

    const isMaxLengthPresent = !!maxLength || maxLength === 0;
    const isCharacterLimitVisible =
      isMaxLengthPresent && valueLength >= maxLength! * 0.85;
    const maxLengthError = unlimitedChars && valueLength > maxLength!;

    const rows = rowsProp ?? SIZE_ROWS[size];

    // --- Handlers ---

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (!isControlled) {
        setValueInternal(e.target.value);
      }
      onChange?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      const trimmedValue = getTrimmedValue(value ?? "", disableTrimOnBlur);

      if (trimmedValue !== value) {
        e.target.value = trimmedValue.toString();
        handleChange(e as unknown as React.ChangeEvent<HTMLTextAreaElement>);
      }

      onBlur?.(e);
    };

    // --- aria-describedby ---

    const ariaDescribedBy =
      [error ? errorId : null, helpText ? helpTextId : null]
        .filter(Boolean)
        .join(" ") || undefined;

    // --- Textarea element ---

    const hasField = !!(label || error || helpText);

    const textareaElement = (
      <PrimitiveTextarea
        ref={ref}
        id={id}
        rows={rows}
        disabled={disabled}
        required={required}
        aria-invalid={!!error || undefined}
        aria-describedby={ariaDescribedBy}
        className={cn(
          // Override shadcn's field-sizing-content and min-h-16 so rows works
          "!field-sizing-normal",
          SIZE_MIN_HEIGHT[size],
          resize === "none" && "resize-none",
          resize === "vertical" && "resize-y",
          nakedTextarea &&
            "border-transparent shadow-none px-0 py-0 focus-visible:border-transparent focus-visible:ring-0",
          !!error && "border-destructive ring-destructive/20 ring-3"
        )}
        value={value}
        onBlur={handleBlur}
        onChange={handleChange}
        {...(isMaxLengthPresent && !unlimitedChars ? { maxLength } : {})}
        {...otherProps}
      />
    );

    const hasPrefixSuffix = !!(prefix || suffix);

    const textareaWithAffixes = hasPrefixSuffix ? (
      <div
        className={cn(
          "flex items-start gap-2 rounded-lg border border-input px-2.5 py-2 focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50",
          !!error && "border-destructive ring-destructive/20 ring-3",
          nakedTextarea && "border-transparent shadow-none"
        )}
      >
        {prefix && (
          <div className="shrink-0 pt-0.5 text-muted-foreground">{prefix}</div>
        )}
        <PrimitiveTextarea
          ref={ref}
          id={id}
          rows={rows}
          disabled={disabled}
          required={required}
          aria-invalid={!!error || undefined}
          aria-describedby={ariaDescribedBy}
          className={cn(
            "!field-sizing-normal border-0 p-0 shadow-none focus-visible:border-0 focus-visible:ring-0",
            SIZE_MIN_HEIGHT[size],
            resize === "none" && "resize-none",
            resize === "vertical" && "resize-y"
          )}
          value={value}
          onBlur={handleBlur}
          onChange={handleChange}
          {...(isMaxLengthPresent && !unlimitedChars ? { maxLength } : {})}
          {...otherProps}
        />
        {suffix && (
          <div className="shrink-0 pt-0.5 text-muted-foreground">{suffix}</div>
        )}
      </div>
    ) : (
      textareaElement
    );

    if (!hasField) {
      return <div className={cn(className)}>{textareaWithAffixes}</div>;
    }

    return (
      <Field
        data-disabled={disabled || undefined}
        data-invalid={!!error || undefined}
        className={className}
      >
        {(label || isCharacterLimitVisible) && (
          <div className="flex items-center justify-between">
            {label && (
              <FieldLabel htmlFor={id} {...labelProps}>
                {label}
                {required && (
                  <span aria-hidden="true" className="text-destructive">
                    *
                  </span>
                )}
              </FieldLabel>
            )}
            {isCharacterLimitVisible && (
              <span
                className={cn(
                  "text-xs tabular-nums",
                  maxLengthError ? "text-destructive" : "text-muted-foreground"
                )}
              >
                {valueLength}/{maxLength}
              </span>
            )}
          </div>
        )}
        <FieldContent>
          {textareaWithAffixes}
          {!!error && <FieldError id={errorId}>{error}</FieldError>}
          {helpText && (
            <FieldDescription id={helpTextId}>{helpText}</FieldDescription>
          )}
        </FieldContent>
      </Field>
    );
  }
);

Textarea.displayName = "Textarea";

export { Textarea };
